/**
 * The "Angebotsliste" — a quote basket that lives entirely in the browser.
 *
 * It is deliberately not the Shopify cart: a quote is a request, not a purchase, so
 * nothing here may reach `cart/add.js` or the checkout. Lines are persisted to
 * localStorage under a versioned key and every mutation republishes the whole list,
 * so any number of custom elements can subscribe without knowing about each other.
 *
 * A line is keyed by variant id, which is what makes "add the same variant twice"
 * an update rather than a duplicate row.
 *
 * A classic script, not an ES module: Shopify serves theme assets from the CDN under
 * hashed filenames, where relative `import` specifiers do not resolve. `OfferList` is
 * shared through `window` instead. The IIFE and the guard keep a second include — the
 * PDP loads this alongside the offer page's copy — from redeclaring anything.
 */
(function () {
  if (window.OfferList) return;

  const STORAGE_KEY = 'offer-list:v1';
  const CHANGE_EVENT = 'offer-list:change';

/**
 * @typedef {object} OfferLine
 * @property {number} variantId
 * @property {number} productId
 * @property {string} handle
 * @property {string} title        Product title.
 * @property {string} variantTitle Option values, e.g. "Navy / M".
 * @property {string} sku
 * @property {string} image        CDN url, already sized.
 * @property {string} url
 * @property {number} price        Undiscounted unit price, in cents.
 * @property {number} quantity
 * @property {number} min          Minimum order quantity for this variant.
 * @property {Array<{minimumQuantity: number, price: number}>} priceBreaks
 * @property {string} [group]      Set on the size lines of one split order (product +
 *                                 colour). The group is priced and held to its minimum
 *                                 as one quantity: 20 S + 30 M is a 50-piece order.
 * @property {number} [groupMin]   Minimum order quantity of the group's combined total.
 */

const OfferList = {
  /** @returns {OfferLine[]} */
  read() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const lines = raw ? JSON.parse(raw) : [];
      return Array.isArray(lines) ? lines : [];
    } catch {
      // Corrupt or unavailable storage (private mode, quota) degrades to an empty list
      // rather than taking the page down with it.
      return [];
    }
  },

  /** @param {OfferLine[]} lines */
  write(lines) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
    } catch (error) {
      console.error('The offer list could not be saved', error);
    }

    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { lines } }));
    return lines;
  },

  /** @param {OfferLine} line */
  add(line) {
    return this.addMany([line]);
  },

  /**
   * Adds lines, or sets existing ones to the incoming quantity.
   *
   * The quantity is *set*, not incremented: the PDP always sends the quantity the
   * customer configured for that variant, and incrementing would silently double it
   * when they go back and adjust their choice.
   *
   * A split order replaces its previous version wholesale, so a size left out of the
   * new split does not linger from the last request.
   *
   * @param {OfferLine[]} incoming
   */
  addMany(incoming) {
    const groups = new Set(incoming.map((line) => line.group).filter(Boolean));
    const lines = this.read().filter((each) => !(each.group && groups.has(each.group)));

    for (const line of incoming) {
      const existing = lines.find((each) => each.variantId === line.variantId);

      if (existing) {
        Object.assign(existing, line, { quantity: Math.max(line.quantity, line.min ?? 1) });
      } else {
        lines.push(line);
      }
    }

    return this.write(lines);
  },

  /** Clamped to the line's floor, so the group minimum holds whatever the caller sends. */
  update(variantId, quantity) {
    const lines = this.read();
    const line = lines.find((each) => each.variantId === variantId);
    if (!line) return lines;

    line.quantity = Math.max(quantity, this.minimumFor(line, lines));
    return this.write(lines);
  },

  /**
   * Immer möglich (pz-patch56). Fällt die Gruppe dadurch unter ihre Mindestmenge, wird die
   * größte verbleibende Zeile aufgefüllt – vorher blieben solche Zeilen ausgegraut hängen.
   */
  remove(variantId) {
    const lines = this.read();
    const line = lines.find((each) => each.variantId === variantId);
    const rest = lines.filter((each) => each.variantId !== variantId);
    if (line && line.group && line.groupMin) {
      const mates = rest.filter((each) => each.group === line.group);
      const total = mates.reduce((sum, each) => sum + each.quantity, 0);
      if (mates.length && total < line.groupMin) {
        const biggest = mates.reduce((a, b) => (b.quantity > a.quantity ? b : a));
        biggest.quantity += line.groupMin - total;
      }
    }
    return this.write(rest);
  },

  clear() {
    return this.write([]);
  },

  /** The quantity the line is priced at: its group's combined total, or its own. */
  groupQuantity(line, lines = this.read()) {
    if (!line.group) return line.quantity;

    return lines
      .filter((each) => each.group === line.group)
      .reduce((total, each) => total + each.quantity, 0);
  },

  /**
   * Lowest quantity the line may be set to. In a group that is whatever keeps the
   * combined total at the minimum, but never below one piece.
   */
  minimumFor(line, lines = this.read()) {
    if (!line.group || !line.groupMin) return line.min ?? 1;

    const others = this.groupQuantity(line, lines) - line.quantity;
    return Math.max(1, line.groupMin - others);
  },

  /** Jede Zeile darf gehen – remove() hält die Gruppen-Mindestmenge über die übrigen Zeilen (pz-patch56). */
  canRemove() {
    return true;
  },

  /** Unit price, honouring the volume tiers the line's (group) quantity reaches. */
  unitPrice(line, quantity = this.groupQuantity(line)) {
    const applicable = (line.priceBreaks ?? [])
      .filter((each) => quantity >= each.minimumQuantity)
      .sort((a, b) => b.minimumQuantity - a.minimumQuantity)[0];

    return applicable ? applicable.price : line.price;
  },

  lineTotal(line) {
    return this.unitPrice(line) * line.quantity;
  },

  count() {
    return this.read().reduce((total, line) => total + line.quantity, 0);
  },

  /** @param {(lines: OfferLine[]) => void} callback */
  subscribe(callback) {
    const handler = (event) => callback(event.detail.lines);
    document.addEventListener(CHANGE_EVENT, handler);

    // A second tab writing to the same key must not leave this one stale.
    const storageHandler = (event) => {
      if (event.key === STORAGE_KEY) callback(this.read());
    };
    window.addEventListener('storage', storageHandler);

    return () => {
      document.removeEventListener(CHANGE_EVENT, handler);
      window.removeEventListener('storage', storageHandler);
    };
  },
};

  window.OfferList = OfferList;
})();
